import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import SchoolSidebar from './SchoolSidebar';
import { SchoolMobileHeader } from './SchoolMobileHeader';
import './SchoolDashboard.css';

function StatementHeaderFooter() {
  const navigate = useNavigate();
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    payment_option_1: '',
    payment_option_2: '',
    contact_message: '',
    thank_you_message: ''
  });

  const [errors, setErrors] = useState({});
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  useEffect(() => {
    const storedSchoolData = localStorage.getItem('schoolData');
    if (!storedSchoolData) {
      navigate('/schoollogin');
      return;
    }
    setSchoolData(JSON.parse(storedSchoolData));
    setLoading(false);
  }, [navigate]);

  useEffect(() => {
    if (schoolData?.id) {
      fetchStatementSettings();
    }
  }, [schoolData?.id]);

  const fetchStatementSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('statement_settings')
        .select('*')
        .eq('school_id', schoolData.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching statement settings:', error);
        return;
      }

      if (data) {
        setFormData({
          payment_option_1: data.payment_option_1 || '',
          payment_option_2: data.payment_option_2 || '',
          contact_message: data.contact_message || '',
          thank_you_message: data.thank_you_message || ''
        });
      } else {
        // Set default values
        setFormData({
          payment_option_1: 'Paybill No: xxxxx -xxxxxx, Account Number – "Student Full Name"',
          payment_option_2: 'First Community Bank | A/C No: xxxxxxxxxxx | South C Branch | A/C Name: Hidaya Academy',
          contact_message: 'For Any Queries About This Statement, Kindly Contact The Office Within Two Weeks Of Receiving This Statement.',
          thank_you_message: 'Thank You For Your Payments , Its Our Pleasure To Continue Serving You!'
        });
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    const newErrors = {};
    if (!formData.payment_option_1.trim()) {
      newErrors.payment_option_1 = 'Payment Option 1 is required';
    }
    if (!formData.thank_you_message.trim()) {
      newErrors.thank_you_message = 'Thank you message is required';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSaving(true);
    try {
      // Check if settings exist
      const { data: existing } = await supabase
        .from('statement_settings')
        .select('id')
        .eq('school_id', schoolData.id)
        .maybeSingle();

      const settingsData = {
        school_id: schoolData.id,
        payment_option_1: formData.payment_option_1.trim(),
        payment_option_2: formData.payment_option_2.trim(),
        contact_message: formData.contact_message.trim(),
        thank_you_message: formData.thank_you_message.trim(),
        updated_at: new Date().toISOString()
      };

      if (existing) {
        // Update existing
        const { error } = await supabase
          .from('statement_settings')
          .update(settingsData)
          .eq('id', existing.id);

        if (error) throw error;
      } else {
        // Insert new
        const { error } = await supabase
          .from('statement_settings')
          .insert(settingsData);

        if (error) throw error;
      }

      // Show success popup
      setShowSuccessPopup(true);
    } catch (error) {
      console.error('Error saving statement settings:', error);
      alert('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="school-dashboard">
      {/* Success Popup */}
      {showSuccessPopup && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          animation: 'fadeIn 0.3s ease'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            borderRadius: '20px',
            padding: '2.5rem',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            textAlign: 'center',
            animation: 'slideInScale 0.4s ease',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Decorative background elements */}
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '200px',
              height: '200px',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              borderRadius: '50%',
              zIndex: 0
            }} />
            <div style={{
              position: 'absolute',
              bottom: '-30px',
              left: '-30px',
              width: '150px',
              height: '150px',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              borderRadius: '50%',
              zIndex: 0
            }} />

            <div style={{ position: 'relative', zIndex: 1 }}>
              {/* Success Icon */}
              <div style={{
                width: '80px',
                height: '80px',
                margin: '0 auto 1.5rem',
                background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                animation: 'scaleIn 0.5s ease',
                boxShadow: '0 10px 30px rgba(16, 185, 129, 0.4)'
              }}>
                <svg
                  width="50"
                  height="50"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="white"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ animation: 'checkmarkDraw 0.6s ease 0.3s both' }}
                >
                  <path d="M20 6L9 17l-5-5" />
                </svg>
              </div>

              {/* Success Message */}
              <h2 style={{
                fontSize: '1.75rem',
                fontWeight: '700',
                color: '#1F2937',
                marginBottom: '1rem',
                animation: 'fadeInUp 0.5s ease 0.2s both'
              }}>
                Settings Saved Successfully!
              </h2>

              <p style={{
                fontSize: '1rem',
                color: '#6B7280',
                marginBottom: '1.5rem',
                animation: 'fadeInUp 0.5s ease 0.3s both'
              }}>
                Statement header and footer settings have been updated.
              </p>

              {/* Close Button */}
              <button
                onClick={() => setShowSuccessPopup(false)}
                style={{
                  padding: '0.75rem 2rem',
                  background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                  animation: 'fadeInUp 0.5s ease 0.5s both'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add CSS animations */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideInScale {
          from {
            opacity: 0;
            transform: scale(0.9) translateY(-20px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }

        @keyframes scaleIn {
          from {
            transform: scale(0);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }

        @keyframes checkmarkDraw {
          from {
            stroke-dasharray: 0, 24;
            opacity: 0;
          }
          to {
            stroke-dasharray: 24, 24;
            opacity: 1;
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>

      <SchoolMobileHeader 
        schoolData={schoolData} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen} 
      />
      
      <SchoolSidebar schoolData={schoolData} />
      
      <div className="main-content" style={{ 
        width: '100%', 
        maxWidth: '100%', 
        overflowX: 'hidden',
        boxSizing: 'border-box',
        padding: '1.5rem'
      }}>
        <div className="content-header" style={{ 
          width: '100%', 
          maxWidth: '100%',
          boxSizing: 'border-box',
          marginBottom: '1.5rem'
        }}>
          <h1>Statement Header & Footer Settings</h1>
          <p>Configure header and footer details for fee statements</p>
        </div>

        <div className="dashboard-section" style={{ 
          width: '100%', 
          maxWidth: '100%',
          boxSizing: 'border-box',
          padding: '2rem'
        }}>
          <form onSubmit={handleSubmit}>
            {/* Header Section */}
            <div style={{ marginBottom: '2rem' }}>
              <h2 style={{ marginBottom: '1rem', color: '#1F2937' }}>Header Settings</h2>
              <div style={{
                background: '#F9FAFB',
                padding: '1.5rem',
                borderRadius: '8px',
                border: '1px solid #E5E7EB'
              }}>
                <p style={{ color: '#6B7280', marginBottom: '1rem' }}>
                  <strong>Note:</strong> Header includes school logo and details from your school profile.
                  To update these, please go to your school settings.
                </p>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                  <div style={{ flex: '1' }}>
                    <p style={{ margin: '0.25rem 0', fontWeight: '600' }}>School Name:</p>
                    <p style={{ margin: '0.25rem 0', color: '#6B7280' }}>{schoolData.school_name || 'Not set'}</p>
                  </div>
                  <div style={{ flex: '1' }}>
                    <p style={{ margin: '0.25rem 0', fontWeight: '600' }}>Address:</p>
                    <p style={{ margin: '0.25rem 0', color: '#6B7280' }}>{schoolData.address || 'Not set'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer Section */}
            <div style={{ marginBottom: '2rem' }}>
              <h2 style={{ marginBottom: '1rem', color: '#1F2937' }}>Footer Settings</h2>
              
              <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                <label htmlFor="payment_option_1">
                  Payment Option 1 *
                </label>
                <input
                  type="text"
                  id="payment_option_1"
                  name="payment_option_1"
                  value={formData.payment_option_1}
                  onChange={handleInputChange}
                  placeholder="e.g., Paybill No: xxxxx -xxxxxx, Account Number – 'Student Full Name'"
                  style={{ 
                    width: '100%', 
                    padding: '0.75rem', 
                    borderRadius: '6px', 
                    border: errors.payment_option_1 ? '2px solid #EF4444' : '1px solid #D1D5DB',
                    fontSize: '1rem'
                  }}
                />
                {errors.payment_option_1 && (
                  <span style={{ color: '#EF4444', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
                    {errors.payment_option_1}
                  </span>
                )}
              </div>

              <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                <label htmlFor="payment_option_2">
                  Payment Option 2
                </label>
                <input
                  type="text"
                  id="payment_option_2"
                  name="payment_option_2"
                  value={formData.payment_option_2}
                  onChange={handleInputChange}
                  placeholder="e.g., First Community Bank | A/C No: xxxxxxxxxxx | South C Branch | A/C Name: Hidaya Academy"
                  style={{ 
                    width: '100%', 
                    padding: '0.75rem', 
                    borderRadius: '6px', 
                    border: '1px solid #D1D5DB',
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                <label htmlFor="contact_message">
                  Contact Message
                </label>
                <textarea
                  id="contact_message"
                  name="contact_message"
                  value={formData.contact_message}
                  onChange={handleInputChange}
                  placeholder="e.g., For Any Queries About This Statement, Kindly Contact The Office Within Two Weeks Of Receiving This Statement."
                  rows="3"
                  style={{ 
                    width: '100%', 
                    padding: '0.75rem', 
                    borderRadius: '6px', 
                    border: '1px solid #D1D5DB',
                    fontSize: '1rem',
                    resize: 'vertical',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

              <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                <label htmlFor="thank_you_message">
                  Thank You Message *
                </label>
                <textarea
                  id="thank_you_message"
                  name="thank_you_message"
                  value={formData.thank_you_message}
                  onChange={handleInputChange}
                  placeholder="e.g., Thank You For Your Payments , Its Our Pleasure To Continue Serving You!"
                  rows="2"
                  style={{ 
                    width: '100%', 
                    padding: '0.75rem', 
                    borderRadius: '6px', 
                    border: errors.thank_you_message ? '2px solid #EF4444' : '1px solid #D1D5DB',
                    fontSize: '1rem',
                    resize: 'vertical',
                    fontFamily: 'inherit'
                  }}
                />
                {errors.thank_you_message && (
                  <span style={{ color: '#EF4444', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
                    {errors.thank_you_message}
                  </span>
                )}
              </div>
            </div>

            {/* Preview Section */}
            <div style={{ 
              marginBottom: '2rem',
              background: '#F9FAFB',
              padding: '1.5rem',
              borderRadius: '8px',
              border: '1px solid #E5E7EB'
            }}>
              <h3 style={{ marginBottom: '1rem', color: '#1F2937' }}>Preview</h3>
              <div style={{
                background: 'white',
                padding: '1.5rem',
                borderRadius: '6px',
                border: '1px solid #E5E7EB',
                fontSize: '0.875rem',
                lineHeight: '1.6'
              }}>
                <div style={{ marginBottom: '1rem', fontWeight: '600', color: '#374151' }}>
                  Payment Options
                </div>
                <div style={{ marginBottom: '0.75rem', paddingLeft: '1rem' }}>
                  1) {formData.payment_option_1 || 'Payment Option 1'}
                </div>
                {formData.payment_option_2 && (
                  <div style={{ marginBottom: '1rem', paddingLeft: '1rem' }}>
                    2) {formData.payment_option_2}
                  </div>
                )}
                {formData.contact_message && (
                  <div style={{ marginTop: '1rem', marginBottom: '0.75rem', color: '#6B7280' }}>
                    {formData.contact_message}
                  </div>
                )}
                {formData.thank_you_message && (
                  <div style={{ marginTop: '1rem', textAlign: 'center', fontWeight: '600', color: '#374151' }}>
                    {formData.thank_you_message}
                  </div>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
              <button
                type="submit"
                disabled={saving}
                style={{
                  padding: '0.75rem 2rem',
                  background: saving 
                    ? '#D1D5DB' 
                    : 'linear-gradient(135deg, #FF8C42 0%, #E67E22 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: saving 
                    ? 'none' 
                    : '0 4px 12px rgba(255, 140, 66, 0.3)',
                  opacity: saving ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!saving) {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 16px rgba(255, 140, 66, 0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!saving) {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 12px rgba(255, 140, 66, 0.3)';
                  }
                }}
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default StatementHeaderFooter;
